import React from 'react'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { Card, CardContent, Stack, Typography } from '@mui/material'
import { type StrategyDataGridRow } from '../../utils/view-specific/strategy/strategy-utils'
import clsx from 'clsx'
import { isProductTypeSpot } from '../../utils/exchange-utils'
import { blue } from '@mui/material/colors'

export interface StrategyComponentAccountSummaryProps {
  rows: StrategyDataGridRow[]
}

const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'Symbol',
    width: 150,
    sortComparator: (v1, v2) => {
      const v1Splits = v1.split('_')
      const v2Splits = v2.split('_')
      if (v1Splits.length > v2Splits.length) {
        return 1
      } else if (v1Splits.length < v2Splits.length) {
        return -1
      }

      for (let i = 0; i < v1Splits.length; i++) {
        const localeCompareResult = v1Splits[i].localeCompare(v2Splits[i], undefined, { numeric: true })
        if (localeCompareResult !== 0) {
          return localeCompareResult
        }
      }
    },
    cellClassName: (params) => {
      return clsx('productType', {
        spot: isProductTypeSpot(params.value as string),
        swap: !isProductTypeSpot(params.value as string),
      })
    }
  },
  {
    field: 'Accounts.buy',
    headerName: 'Buy: Account, (Tradability, MR)',
    flex: 1,
    minWidth: 200,
    maxWidth: 600,
    align: 'left',
    sortable: false,
    valueGetter: (_value, row, _column, _apiRef) => {
      let tradable: string = ''
      for (const rowKey in row) {
        if (rowKey.startsWith('Tradability.') && rowKey.includes('tradable')) {
          tradable = row[rowKey].split(' | ')[0]
          break
        }
      }
      return formatDisplayValue(row.id, tradable, row['Accounts.buy'])
    }
  },
  {
    field: 'Accounts.sell',
    headerName: 'Sell: Account, (Tradability, MR)',
    flex: 1,
    minWidth: 200,
    maxWidth: 600,
    align: 'left',
    sortable: false,
    valueGetter: (_value, row, _column, _apiRef) => {
      let tradable: string = ''
      for (const rowKey in row) {
        if (rowKey.startsWith('Tradability.') && rowKey.includes('tradable')) {
          tradable = row[rowKey].split(' | ')[1]
          break
        }
      }
      return formatDisplayValue(row.id, tradable, row['Accounts.sell'])
    }
  },
]

function formatDisplayValue (symbol: string, tradable: string, value: string): string {
  if (value == null || value.length === 0 || !value.includes('[') || !value.includes(']')) {
    return ''
  }

  const output: string[] = []

  if (symbol.endsWith('_SPOT')) {
    const accountSplits = value.substring(1, value.length - 1).split('][')
    for (const accountSplit of accountSplits) {
      output.push(accountSplit?.substring(8))
    }
  } else {
    const accountMRSplits = value.substring(1, value.length - 1).split('][')
    for (const accountMRSplit of accountMRSplits) {
      const splits = accountMRSplit.split(', ')
      output.push(splits[0]?.substring(8) + ' (' + tradable + ', ' + splits[1]?.substring(3) + ')')
    }
  }

  return output.join(' => ')
}

const StrategyComponentAccountSummary: React.FC<StrategyComponentAccountSummaryProps> = (props) => {
  const { rows } = props

  return (
    <Card variant='outlined' sx={{ borderRadius: '0' }}>
      <CardContent>
        <Stack gap={1}>
          <Typography variant='h6' color='secondary.light'>
            Accounts (Tradability, MR)
          </Typography>

          <DataGridPro
            disableRowSelectionOnClick
            hideFooter
            columnHeaderHeight={36}
            rowHeight={24}
            columns={columns}
            rows={rows}
            initialState={{
              sorting: {
                sortModel: [{ field: 'id', sort: 'asc' }]
              }
            }}
            sx={{
              '& .productType.spot': {
                color: blue[500],
              },
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  )
}

export default StrategyComponentAccountSummary
